import { useEffect, useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { colors } from "@/theme/colors";
import { useAuth } from "@/auth/useAuth";

const NAV = [
  { to: "/", label: "Home", short: "H" },
  { to: "/cycles", label: "Ciclos", short: "C" },
  { to: "/skus", label: "SKUs", short: "S" },
];

/**
 * Document-first shell per DESIGN.md §4. The sidebar collapses to a thin icon
 * rail so review screens can reclaim the horizontal space (Feedback V2 rev02
 * r2-1/r2-5: even fullscreen left too little room for the document). Collapsed
 * state is persisted so the choice survives reloads.
 */
export function Layout() {
  const { user, loading } = useAuth();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem("marcador.sidebarCollapsed") === "true";
  });
  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("marcador.sidebarCollapsed", String(collapsed));
    }
  }, [collapsed]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: collapsed ? "48px 1fr" : "280px 1fr",
        height: "100vh",
        background: colors.shell,
      }}
    >
      <aside
        style={{
          background: colors.panel,
          borderRight: `1px solid ${colors.line}`,
          padding: collapsed ? "var(--space-base) 6px" : "var(--space-base)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-base)",
          alignItems: collapsed ? "center" : "stretch",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            gap: 8,
          }}
        >
          {!collapsed && (
            <h1
              style={{
                fontSize: "var(--fs-title)",
                lineHeight: "var(--lh-title)",
                letterSpacing: "0.01em",
                margin: 0,
              }}
            >
              Marcador Natura
            </h1>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((on) => !on)}
            title={collapsed ? "Expandir menu" : "Recolher menu"}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            style={{
              flex: "none",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${colors.line}`,
              borderRadius: "var(--radius-sm)",
              background: colors.shell,
              color: colors.text,
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>
        <nav
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            width: "100%",
            alignItems: collapsed ? "center" : "stretch",
          }}
        >
          {NAV.map((item) => (
            <NavItem key={item.to} to={item.to} collapsed={collapsed} label={item.label}>
              {collapsed ? item.short : item.label}
            </NavItem>
          ))}
        </nav>
        {!collapsed && (
          <div
            style={{
              marginTop: "auto",
              fontSize: "var(--fs-ui)",
              color: colors.textMuted,
            }}
          >
            {loading ? "…" : user?.email ?? "Não autenticado"}
          </div>
        )}
      </aside>
      <main style={{ overflow: "auto", padding: "var(--space-roomy)" }}>
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({
  to,
  children,
  collapsed,
  label,
}: {
  to: string;
  children: React.ReactNode;
  collapsed: boolean;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      end
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        width: collapsed ? 34 : "auto",
        height: collapsed ? 34 : "auto",
        padding: collapsed ? 0 : "8px 10px",
        borderRadius: "var(--radius-sm)",
        textDecoration: "none",
        color: isActive ? colors.onPrimary : colors.text,
        background: isActive ? colors.primary : "transparent",
        fontSize: "var(--fs-body)",
        fontWeight: 500,
      })}
    >
      {children}
    </NavLink>
  );
}
